import { ReactNode } from "react";
import classNames from "classnames";
import { ScheduleTemplateListItem } from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import { ScheduleTemplateStatus } from "@common/types/domains/accounting/schedule";
import { AccountingSchedulesViewColumn } from "@common/types/views/accountingSchedulesView";
import { Entity } from "@common/types/entity";
import { Button } from "@design-system/Button/Button";
import { EntityAvatarGroup } from "@components/avatar/EntityAvatarGroup";
import { ScheduleGroup } from "./scheduleListHelpers";
import { SCHEDULE_TEMPLATE_STATUS_LABELS } from "./SchedulePoliciesFiltersControls";
import {
    ScheduleRowActionsCell,
    ScheduleTemplateActions,
} from "./ScheduleRowActionsCell";
import { schedulesTableViewConfig } from "./schedulesTableViewConfig";
import styles from "./SchedulesTable.module.scss";

type BodyColumn = Exclude<
    AccountingSchedulesViewColumn,
    AccountingSchedulesViewColumn.GROUP | AccountingSchedulesViewColumn.ACTIONS
>;

interface SchedulesTableProps extends ScheduleTemplateActions {
    groups: ScheduleGroup[];
    columns: AccountingSchedulesViewColumn[];
    entitiesById: Map<number, Entity>;
}

const COLUMN_CLASS_NAMES: Partial<
    Record<AccountingSchedulesViewColumn, string>
> = {
    [AccountingSchedulesViewColumn.JOURNAL_ENTRIES]:
        styles.journalEntriesColumn,
    [AccountingSchedulesViewColumn.ENTITIES]: styles.entitiesColumn,
    [AccountingSchedulesViewColumn.STATUS]: styles.statusColumn,
};

function isBodyColumn(
    columnId: AccountingSchedulesViewColumn,
): columnId is BodyColumn {
    return (
        columnId !== AccountingSchedulesViewColumn.GROUP &&
        columnId !== AccountingSchedulesViewColumn.ACTIONS
    );
}

function resolveEntities(
    template: ScheduleTemplateListItem,
    entitiesById: Map<number, Entity>,
): Entity[] {
    return template.entityIds.flatMap((entityId) => {
        const entity = entitiesById.get(entityId);
        return entity ? [entity] : [];
    });
}

function renderBodyCell(options: {
    columnId: BodyColumn;
    template: ScheduleTemplateListItem;
    entitiesById: Map<number, Entity>;
    onViewApplied: (template: ScheduleTemplateListItem) => void;
}): ReactNode {
    const { columnId, template, entitiesById, onViewApplied } = options;
    switch (columnId) {
        case AccountingSchedulesViewColumn.NAME:
            return template.name;
        case AccountingSchedulesViewColumn.DESCRIPTION:
            return template.description ?? "";
        case AccountingSchedulesViewColumn.JOURNAL_ENTRIES:
            return (
                <Button
                    variant="secondary"
                    size="sm"
                    className={styles.countButton}
                    disabled={!template.journalEntryCount}
                    onClick={() => onViewApplied(template)}
                >
                    {template.journalEntryCount}
                </Button>
            );
        case AccountingSchedulesViewColumn.ENTITIES:
            return (
                <EntityAvatarGroup
                    entities={resolveEntities(template, entitiesById)}
                    max={3}
                    size="xs"
                />
            );
        case AccountingSchedulesViewColumn.STATUS:
            return (
                <span
                    className={classNames({
                        [styles.archivedStatus]:
                            template.status !== ScheduleTemplateStatus.active,
                    })}
                >
                    {SCHEDULE_TEMPLATE_STATUS_LABELS[template.status]}
                </span>
            );
    }
}

export const SchedulesTable: React.FC<SchedulesTableProps> = ({
    groups,
    columns,
    entitiesById,
    onApply,
    onEdit,
    onViewApplied,
    onDuplicate,
    onArchive,
    onRestore,
    onDelete,
}) => {
    const bodyColumns = columns.filter(isBodyColumn);
    const hasGroupColumn = columns.includes(
        AccountingSchedulesViewColumn.GROUP,
    );

    return (
        <table className={styles.schedulesTable}>
            <thead>
                <tr>
                    {hasGroupColumn && (
                        <th
                            className={classNames(
                                styles.th,
                                styles.groupColumn,
                            )}
                        >
                            {
                                schedulesTableViewConfig.columns[
                                    AccountingSchedulesViewColumn.GROUP
                                ].header
                            }
                        </th>
                    )}
                    {bodyColumns.map((columnId) => (
                        <th
                            key={columnId}
                            className={classNames(
                                styles.th,
                                COLUMN_CLASS_NAMES[columnId],
                            )}
                        >
                            {schedulesTableViewConfig.columns[columnId].header}
                        </th>
                    ))}
                    <th
                        className={classNames(styles.th, styles.actionsColumn)}
                        aria-label="Actions"
                    />
                </tr>
            </thead>
            {groups.map((group) => (
                <tbody key={group.name}>
                    {group.templates.map((template, templateIndex) => (
                        <tr key={template.id} className={styles.row}>
                            {hasGroupColumn && templateIndex === 0 && (
                                <td
                                    className={classNames(
                                        styles.td,
                                        styles.groupCell,
                                    )}
                                    rowSpan={group.templates.length}
                                >
                                    {group.name}
                                </td>
                            )}
                            {bodyColumns.map((columnId) => (
                                <td key={columnId} className={styles.td}>
                                    {renderBodyCell({
                                        columnId,
                                        template,
                                        entitiesById,
                                        onViewApplied,
                                    })}
                                </td>
                            ))}
                            <td className={styles.td}>
                                <ScheduleRowActionsCell
                                    template={template}
                                    onApply={onApply}
                                    onEdit={onEdit}
                                    onViewApplied={onViewApplied}
                                    onDuplicate={onDuplicate}
                                    onArchive={onArchive}
                                    onRestore={onRestore}
                                    onDelete={onDelete}
                                    triggerClassName={styles.rowActions}
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            ))}
        </table>
    );
};
